import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { ProductPolicyApi, SettingsApi } from 'vrlcm-gui-api';
import {
  EnvironmentState,
  InfraProperties,
  PolicyProduct,
  ProductBinary,
  VcfInfraProperties
} from 'vrlcm-gui-types';
import {
  isVcfEnabledEnvironment,
  safeJsonParse,
  toSafeSelectedProducts,
  toSafeUIData
} from 'vrlcm-gui-utils';
import {
  patchEnvironmentState,
  patchEnvironmentStateFailed
} from '../../../actions/workflows/EnvironmentWorkflow';
import {
  initializeStepSelectProductFailed,
  initializeStepSelectProductSuccess,
  SubmitStepSelectProduct
} from '../../../actions/workflows/steps/SelectProduct';
import * as Constants from '../../../constants/action-constants';
import { TOTAL_STEPS } from '../../../constants/AppConstants';
import { StoreState } from '../../../types';
import { UIEnvironmentState } from '../../../types/Environments';
import { isVidm } from '../../../utils/Helpers';
import { extractApiErrorMessageFromAxiosErr } from 'vrlcm-gui-utils';

function* initializeStepSelectProduct() {
  try {
    // const infraProperties: InfraProperties = yield select(
    //   (state: StoreState) =>
    //     state.environmentWorkflow.state.environmentRequestDTO.infrastructure
    //       .properties
    // );

    const environmentState: EnvironmentState = yield select(
      (state: StoreState) => state.environmentWorkflow.state
    );

    const infraProperties: InfraProperties =
      environmentState.environmentRequestDTO.infrastructure.properties;

    const isCloudProxyEnvironment = toSafeUIData(environmentState.uiData)
      .isCloudProxyEnvironment;

    const vcfProperties = safeJsonParse<VcfInfraProperties>(
      infraProperties.vcfProperties
    );
    let products: PolicyProduct[] = [];
    let binaryMappings: ProductBinary[] = [];
    [products, binaryMappings] = yield all([
      isCloudProxyEnvironment
        ? call(ProductPolicyApi.getProxyPolicies)
        : call(ProductPolicyApi.getProductPolicies, vcfProperties?.vcfEnabled),
      call(SettingsApi.getProductBinaries)
    ]);
    const environments: UIEnvironmentState[] = yield select(
      (state: StoreState) => state.environments.items.completed
    );
    const existingVcfDeployedProductIds = environments
      .filter(e => e.requestState === 'COMPLETED')
      .filter(e => {
        return isVcfEnabledEnvironment(e.environmentRequestDTO);
      })
      .map(e =>
        e.environmentRequestDTO.products.map(p =>
          p.id === 'vra' ? `${p.id}:${p.version}` : p.id
        )
      )
      .reduce((acc, cur) => acc.concat(cur), [])
      .filter(p => !['vrni', 'vrbc', 'vssc', 'vro'].includes(p.split(':')[0]));
    const globalenvironment = environments.find(
      e => e.environmentRequestDTO.environmentId === 'globalenvironment'
    );
    const existingVidm = globalenvironment?.environmentRequestDTO.products.find(
      p => p.id === 'vidm'
    );
    yield put(
      initializeStepSelectProductSuccess(
        products,
        binaryMappings,
        existingVcfDeployedProductIds,
        existingVidm
      )
    );
  } catch (e) {
    const errorMessage = extractApiErrorMessageFromAxiosErr(e as any) ?? '';
    yield put(initializeStepSelectProductFailed(errorMessage));
  }
}

function* watchForSelectProduct() {
  yield takeLatest(
    Constants.INITIALIZE_STEP_SELECT_PRODUCT,
    initializeStepSelectProduct
  );
}

function* submitStepSelectProduct(action: SubmitStepSelectProduct) {
  try {
    const { state } = action.payload;
    const vidm = isVidm(state.environmentRequestDTO.products);
    const selectedProducts = toSafeSelectedProducts(
      state.environmentRequestDTO
    );
    const isImport = selectedProducts.every(p => p.type === 'import');
    const uiData = toSafeUIData(state.uiData);
    let totalSteps = vidm ? TOTAL_STEPS.vidm : TOTAL_STEPS.regular;
    if (isImport) {
      totalSteps = state.requestType === 'ADD_PRODUCT' ? 3 : 4;
    } else {
      totalSteps = state.requestType === 'ADD_PRODUCT' ? 9 : totalSteps;
    }
    uiData.totalSteps = totalSteps;
    const nextState = { ...state, uiData: JSON.stringify(uiData) };
    yield put(patchEnvironmentState(nextState));
  } catch (e) {
    yield put(patchEnvironmentStateFailed());
  }
}

function* watchForSubmitSelectProduct() {
  yield takeLatest(
    Constants.SUBMIT_STEP_SELECT_PRODUCT,
    submitStepSelectProduct
  );
}

export default function* selectProductSaga() {
  yield all([watchForSelectProduct(), watchForSubmitSelectProduct()]);
}
